/**
 * Cart Drawer
 * Replaces the legacy .cart-popup-wrapper notification with a full AJAX slide-out cart.
 * Written in the same jQuery/theme.* module idiom as the rest of assets/theme.js so it can
 * safely coexist with theme.Product / theme.product_quickview / theme.product_card, which are
 * hooked (in assets/theme.js) to call theme.cart_drawer.renderAndOpen() after a successful add.
 *
 * The jQuery alias is named `jq` (not `$`) on purpose: this file is deferred alongside
 * assets/theme.js, which also opens with `(function ($) {...})(jQuery)` — using the same `$`
 * name here risks scope-naming collisions in some environments. `jq` avoids that entirely.
 */
window.theme = window.theme || {};

theme.cart_drawer = (function (jq) {
    var selectors = {
        root: '#CartDrawer',
        overlay: '[data-cart-drawer-overlay]',
        panel: '[data-cart-drawer-panel]',
        close: '[data-cart-drawer-close]',
        closeLink: '[data-cart-drawer-close-link]',
        error: '[data-cart-drawer-error]',
        body: '[data-cart-drawer-body]',
        item: '[data-cart-drawer-item]',
        itemLoading: '[data-cart-drawer-item-loading]',
        qtyInput: '[data-cart-drawer-qty-input]',
        qtyPlus: '[data-cart-drawer-qty-plus]',
        qtyMinus: '[data-cart-drawer-qty-minus]',
        remove: '[data-cart-drawer-remove]',
        note: '[data-cart-drawer-note]',
        terms: '[data-cart-drawer-terms]',
        crossSell: '[data-cart-drawer-cross-sell]',
        quickAdd: '[data-cart-drawer-quick-add]',
        countBadge: '[data-cart-count]'
    };

    var classes = {
        bodyLock: 'cart-drawer-lock-scroll'
    };

    var isBusy = false;
    var lastFocusedElement = null;
    var crossSellLoadedFor = null;
    var noteTimer = null;
    var bound = false;

    /**
     * Always re-queries the live DOM instead of relying on a cached reference captured at
     * init time, so this keeps working even if init() ran before #CartDrawer existed, or if
     * the node was ever replaced outright rather than just having its innerHTML swapped.
     */
    function getRoot() {
        return jq(selectors.root);
    }

    function init() {
        if (bound) return;
        bound = true;

        bindDelegatedEvents();
        maybeLoadCrossSell();

        // Dawn's quick-order-list / quick-add-bulk components (used by the collection-grid
        // "Quick Shop" button for multi-variant products) update #CartDrawer's HTML directly
        // via their own Sections Rendering API call, bypassing applyData()/injectDrawerHtml()
        // entirely — so cross-sell is left stale unless we pick it up here. Both components
        // publish PUB_SUB_EVENTS.cartUpdate (assets/global.js) once that swap is done.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
                maybeLoadCrossSell();
            });
        }
    }

    /**
     * Delegated from `document` (not from the #CartDrawer node) on purpose: this makes
     * binding independent of whether #CartDrawer exists in the DOM yet at init() time, and
     * independent of any later full-node replacement (only innerHTML is normally swapped by
     * injectDrawerHtml(), but delegating from document is the more robust choice regardless).
     */
    function bindDelegatedEvents() {
        jq(document).on('keydown', function (evt) {
            if (evt.key === 'Escape' && isOpen()) {
                close();
            }
        });

        jq(document).on('click', selectors.root + ' ' + selectors.overlay, function (evt) {
            evt.preventDefault();
            close();
        });

        jq(document).on('click', selectors.close + ', ' + selectors.closeLink, function (evt) {
            evt.preventDefault();
            close();
        });

        jq(document).on('keydown', selectors.root + ' ' + selectors.panel, function (evt) {
            if (evt.key === 'Tab') {
                trapTab(evt);
            }
        });

        jq(document).on('click', selectors.qtyPlus, function (evt) {
            evt.preventDefault();
            stepQuantity(jq(this), 1);
        });

        jq(document).on('click', selectors.qtyMinus, function (evt) {
            evt.preventDefault();
            stepQuantity(jq(this), -1);
        });

        jq(document).on('change', selectors.qtyInput, function () {
            var $item = jq(this).closest(selectors.item);
            var qty = parseInt(jq(this).val(), 10);
            if (isNaN(qty) || qty < 0) qty = 0;
            changeLine($item, qty);
        });

        jq(document).on('click', selectors.remove, function (evt) {
            evt.preventDefault();
            var $item = jq(this).closest(selectors.item);
            changeLine($item, 0);
        });

        jq(document).on('input', selectors.note, function () {
            var $textarea = jq(this);
            clearTimeout(noteTimer);
            noteTimer = setTimeout(function () {
                updateNote($textarea.val());
            }, 600);
        });

        jq(document).on('click', selectors.quickAdd, function (evt) {
            evt.preventDefault();
            quickAdd(jq(this));
        });
    }

    /* ---------------------------------------------------------------- */
    /* Open / close                                                      */
    /* ---------------------------------------------------------------- */

    function isOpen() {
        return getRoot().attr('aria-hidden') === 'false';
    }

    function open() {
        var $root = getRoot();
        if (!$root.length || isOpen()) return;

        lastFocusedElement = document.activeElement;

        $root.attr('aria-hidden', 'false');
        jq('html').addClass(classes.bodyLock);

        var $panel = $root.find(selectors.panel);
        window.setTimeout(function () {
            $panel.trigger('focus');
        }, 50);
    }

    function close() {
        var $root = getRoot();
        if (!$root.length || !isOpen()) return;

        $root.attr('aria-hidden', 'true');
        jq('html').removeClass(classes.bodyLock);
        hideError();

        if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
            lastFocusedElement.focus();
        }
    }

    function trapTab(evt) {
        var $panel = getRoot().find(selectors.panel);
        var $focusable = $panel
            .find('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
            .filter(':visible');

        if (!$focusable.length) return;

        var first = $focusable[0];
        var last = $focusable[$focusable.length - 1];

        if (evt.shiftKey && document.activeElement === first) {
            evt.preventDefault();
            last.focus();
        } else if (!evt.shiftKey && document.activeElement === last) {
            evt.preventDefault();
            first.focus();
        }
    }

    /* ---------------------------------------------------------------- */
    /* Rendering                                                         */
    /* ---------------------------------------------------------------- */

    /**
     * Applies a Sections Rendering API response and opens the drawer.
     * @param {Object} data
     * @param {Object} [data.sections] - { 'cart-drawer': '<html>...' } as returned by
     *   /cart/add.js, /cart/change.js or /cart/update.js when called with `sections=cart-drawer`.
     * @param {number} [data.item_count] - cart.item_count, used to refresh the header badge.
     * @param {number|string} [data.product_id] - product just added, used to seed cross-sell.
     */
    function renderAndOpen(data) {
        if (!getRoot().length) return;
        applyData(data);
        open();
    }

    /**
     * Opens the drawer immediately using its last-known content (instant, no flicker),
     * then silently refreshes it with a fresh Sections Rendering API call so the cart
     * icon always reflects any changes made in another tab/window.
     * Used by the header cart icon (desktop hover trigger + mobile trigger), replacing
     * the legacy dropdown-cart preview.
     */
    function refreshAndOpen() {
        if (!getRoot().length) return;

        open();

        jq.ajax({
            url: window.routes.cart_update_url,
            method: 'POST',
            dataType: 'json',
            data: {
                sections: 'cart-drawer',
                sections_url: window.location.pathname
            }
        }).done(function (cart) {
            applyData({ sections: cart.sections, item_count: cart.item_count });
        });
    }

    function applyData(data) {
        if (!data) return;

        if (data.sections && data.sections['cart-drawer']) {
            injectDrawerHtml(data.sections['cart-drawer']);
        }

        if (typeof data.item_count === 'number') {
            updateCartCountBadge(data.item_count);
        }

        if (data.product_id) {
            maybeLoadCrossSell(data.product_id, data.product_url);
        } else {
            maybeLoadCrossSell();
        }
    }

    function injectDrawerHtml(html) {
        var $root = getRoot();
        var wasTermsChecked = $root.find(selectors.terms).is(':checked');
        // The server-rendered cart-drawer section never includes cross-sell cards (those are
        // filled in client-side only), so carry the current cross-sell markup across the
        // re-render instead of letting it go empty — an empty container is hidden by the
        // `:empty` CSS rule, which is what caused the cross-sell block to flash away and
        // reappear on every drawer open.
        var existingCrossSellHtml = $root.find(selectors.crossSell).html();

        var $fresh = jq('<div>').html(html).find(selectors.root);
        if (!$fresh.length) return;

        $root.html($fresh.html());

        if (existingCrossSellHtml) {
            $root.find(selectors.crossSell).html(existingCrossSellHtml);
        }

        if (wasTermsChecked) {
            reapplyTermsChecked();
        }

        refreshLazyImages();
    }

    function updateCartCountBadge(count) {
        jq(selectors.countBadge).text(count);
    }

    function reapplyTermsChecked() {
        var $checkbox = getRoot().find(selectors.terms);
        if (!$checkbox.length) return;
        $checkbox.prop('checked', true);
        $checkbox.closest('.terms_conditions_wrapper').next().removeClass('disabled');
    }

    function refreshLazyImages() {
        getRoot()
            .find('img[data-src]')
            .each(function () {
                if (this.dataset.src && this.src !== this.dataset.src) {
                    this.src = this.dataset.src;
                }
            });
    }

    /* ---------------------------------------------------------------- */
    /* Line item quantity / remove                                      */
    /* ---------------------------------------------------------------- */

    function stepQuantity($button, delta) {
        var $item = $button.closest(selectors.item);
        var $input = $item.find(selectors.qtyInput);
        var qty = parseInt($input.val(), 10);
        if (isNaN(qty)) qty = 0;
        qty = Math.max(0, qty + delta);
        $input.val(qty);
        changeLine($item, qty);
    }

    function changeLine($item, quantity) {
        if (isBusy || !$item.length) return;

        isBusy = true;
        hideError();
        toggleItemLoading($item, true);

        jq.ajax({
            url: window.routes.cart_change_url,
            method: 'POST',
            dataType: 'json',
            data: {
                line: $item.data('line'),
                quantity: quantity,
                sections: 'cart-drawer',
                sections_url: window.location.pathname
            }
        })
            .done(function (cart) {
                applyData({ sections: cart.sections, item_count: cart.item_count });
            })
            .fail(function () {
                showError();
                toggleItemLoading($item, false);
            })
            .always(function () {
                isBusy = false;
            });
    }

    function toggleItemLoading($item, isLoading) {
        $item.find(selectors.itemLoading).attr('hidden', !isLoading);
    }

    /* ---------------------------------------------------------------- */
    /* Note                                                              */
    /* ---------------------------------------------------------------- */

    function updateNote(note) {
        jq.ajax({
            url: window.routes.cart_update_url,
            method: 'POST',
            dataType: 'json',
            data: { note: note }
        }).fail(function () {
            showError();
        });
    }

    /* ---------------------------------------------------------------- */
    /* Cross-sell                                                        */
    /* ---------------------------------------------------------------- */

    /**
     * Cross-sell loads in three tiers, each one only tried if the previous returned no
     * meaningful markup:
     *   1. Shopify Product Recommendations, intent=complementary ("goes well with").
     *   2. Same API, intent=related (broader — doesn't need order-history data, so it's
     *      more likely to have something for new/low-volume products).
     *   3. Same-collection fallback (sections/cart-drawer-related.liquid) — plain Liquid,
     *      always available if the product belongs to a collection with other items.
     * If all three come back empty, the cross-sell block is simply hidden (CSS :empty rule).
     */
    function maybeLoadCrossSell(productId, productUrl) {
        var $container = getRoot().find(selectors.crossSell);
        if (!$container.length) return;

        if (!productId) {
            // cart.items (sections/cart-drawer.liquid) is oldest-first with no reverse
            // filter, so the most recently added line is the last one, not the first.
            var $lastItem = getRoot().find(selectors.item).last();
            productId = $lastItem.data('product-id');
            productUrl = $lastItem.data('product-url');
        }

        if (!productId) return;

        // injectDrawerHtml() wipes this container back to empty on every re-render (the
        // server-rendered drawer HTML never includes cross-sell cards, only JS fills them
        // in) — so even for the same productId, only skip re-fetching if cards are still
        // actually present; otherwise this container was just wiped and needs refilling.
        var alreadyRendered = $container.find('.cart-drawer-cross-sell__item').length > 0;
        if (crossSellLoadedFor === productId && alreadyRendered) return;
        crossSellLoadedFor = productId;

        fetchRecommendations($container, productId, 'complementary', function (html) {
            if (isMeaningfulHtml(html)) {
                applyCrossSellHtml($container, html);
                return;
            }

            fetchRecommendations($container, productId, 'related', function (relatedHtml) {
                if (isMeaningfulHtml(relatedHtml)) {
                    applyCrossSellHtml($container, relatedHtml);
                    return;
                }

                if (!productUrl) {
                    $container.empty();
                    return;
                }

                fetchCollectionFallback(productUrl, function (fallbackHtml) {
                    applyCrossSellHtml($container, fallbackHtml || '');
                });
            });
        });
    }

    /**
     * The recommendations/section-rendering endpoints always wrap their output in a
     * `<div id="shopify-section-...">` container even when the section itself renders
     * nothing — so an empty-vs-non-empty string check alone can't tell real product cards
     * apart from an empty wrapper. Check for the card class the shared
     * snippets/cart-drawer-cross-sell-card.liquid always emits instead.
     */
    function isMeaningfulHtml(html) {
        return !!(html && html.indexOf('cart-drawer-cross-sell__item') !== -1);
    }

    function applyCrossSellHtml($container, html) {
        $container.html(html);
        refreshLazyImages();
    }

    function fetchRecommendations($container, productId, intent, callback) {
        var limit = $container.data('limit') || 4;
        var url =
            window.router +
            '/recommendations/products?section_id=cart-drawer-cross-sell&product_id=' +
            encodeURIComponent(productId) +
            '&limit=' +
            encodeURIComponent(limit) +
            '&intent=' +
            intent;

        jq.get(url)
            .done(function (html) {
                callback(html);
            })
            .fail(function () {
                callback('');
            });
    }

    function fetchCollectionFallback(productUrl, callback) {
        var separator = productUrl.indexOf('?') > -1 ? '&' : '?';

        jq.get(productUrl + separator + 'section_id=cart-drawer-related')
            .done(function (html) {
                callback(html);
            })
            .fail(function () {
                callback('');
            });
    }

    function quickAdd($button) {
        if ($button.is('[disabled]')) return;

        var variantId = $button.data('variant-id');
        if (!variantId) return;

        $button.attr('disabled', true);

        jq.ajax({
            url: window.router + '/cart/add.js',
            method: 'POST',
            dataType: 'json',
            data: {
                id: variantId,
                quantity: 1,
                sections: 'cart-drawer',
                sections_url: window.location.pathname
            }
        })
            .done(function (item) {
                jq.getJSON('/cart.js').then(function (cart) {
                    applyData({
                        sections: item.sections,
                        item_count: cart.item_count,
                        product_id: item.product_id,
                        product_url: item.url
                    });
                });
            })
            .fail(function () {
                showError();
            })
            .always(function () {
                $button.removeAttr('disabled');
            });
    }

    /* ---------------------------------------------------------------- */
    /* Errors                                                            */
    /* ---------------------------------------------------------------- */

    function showError() {
        var $error = getRoot().find(selectors.error);
        if (!$error.length) return;
        $error.text($error.data('message'));
        $error.removeAttr('hidden');
    }

    function hideError() {
        getRoot().find(selectors.error).attr('hidden', true);
    }

    return {
        init: init,
        open: open,
        close: close,
        renderAndOpen: renderAndOpen,
        refreshAndOpen: refreshAndOpen
    };
})(jQuery);

// Bound with native DOM APIs (not jQuery's `$(fn)` ready-shorthand) so init() is guaranteed
// to fire regardless of jQuery's own ready-queue timing; init()'s internal `bound` guard
// makes calling it more than once from these overlapping triggers harmless.
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', theme.cart_drawer.init);
} else {
    theme.cart_drawer.init();
}
window.addEventListener('load', theme.cart_drawer.init);
